import { makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteItemSection } from '@paintscout/util/builder'
import classnames from 'classnames'
import React from 'react'
import { useQuote } from '../context'
import FilesByItem from '../Quote/FilesByItem'
import HideAmountButton from './HideAmountButton'
import OutlinedTableCard from './OutlinedTableCard'

export interface MediaSectionNextProps {
  /**
   * Which document this Media section belongs to. Both documents use the same card frame; this only
   * picks whose files are visible — see the component docs.
   */
  variant: 'quote' | 'work-order'
  /** Whether the file groups are hidden (the Hide/Show toggle's state). */
  collapsed: boolean
  onToggle: (event: React.MouseEvent<HTMLElement>) => void
  /** Card title. Defaults to "Media". */
  title?: string
  /**
   * Which sections' files this card shows. Defaults to every section; the work order splits Additional
   * Work and Options Added into their own Media cards (ENG-2078).
   */
  sections?: QuoteItemSection[]
  /** Leave out items an earlier Media card already shows (see `FilesByItem`). */
  excludeItemsInSections?: QuoteItemSection[]
  /**
   * Carry the work order's `#pictures` scroll anchor. On by default; the work order turns it off on
   * its added-work Media cards so the id stays unique.
   */
  scrollAnchor?: boolean
}

/** Every section that can carry files, in the order the documents list them. Same set for both. */
const FILE_SECTIONS: QuoteItemSection[] = ['bid', 'additional', 'added-options', 'pending', 'options']

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {},
      // The card body needs the padding the table sections get from their rows; the file grid has none
      // of its own, so without this the thumbnails sit against the card border.
      quoteBody: {
        padding: theme.spacing(2)
      }
    }
  },
  { name: 'MediaSectionNext' }
)

/**
 * The Media section for the redesigned (estimateNext) documents.
 *
 * Both documents use the same frame: an {@link OutlinedTableCard} with a light header band, the title,
 * and a Hide/Show pill — the same shape as the Products and Options sections beside it. The work order
 * carried an icon + bare heading row at first, but design dropped it: outside a card the block read as a
 * different kind of thing from the sections above it (ENG-1377).
 *
 * The file groups themselves are the shared `FilesByItem` in both, so reordering, the lightbox, and
 * the editor's file dialogs behave exactly as they do in the classic views. The consumer differs with
 * the document, which is what decides whose files are visible: the customer sees `customer` files on
 * the estimate, the crew sees `crew` files on the work order.
 */
export default function MediaSectionNext({
  variant,
  collapsed,
  onToggle,
  title,
  sections = FILE_SECTIONS,
  excludeItemsInSections,
  scrollAnchor = true
}: MediaSectionNextProps) {
  const classes = useStyles()
  const { isEditable } = useQuote()
  const { __ } = useTranslation()
  const isWorkOrder = variant === 'work-order'

  const toggle = isEditable ? (
    <HideAmountButton visible={!collapsed} visibleLabel={__('Hide')} hiddenLabel={__('Show')} onToggle={onToggle} />
  ) : undefined

  return (
    <FilesByItem
      // `FilesByItem` renders this only when it has files, and the card below supplies its own
      // heading, so the legacy FormSectionTitle is never used on this path.
      FormSectionTitle={null}
      sections={sections}
      excludeItemsInSections={excludeItemsInSections}
      consumer={isWorkOrder ? 'crew' : 'customer'}
      renderSection={({ body }) => (
        // `id` kept from the legacy work-order Media block, which used it as a scroll anchor.
        <div className={classes.root} id={isWorkOrder && scrollAnchor ? 'pictures' : undefined}>
          <OutlinedTableCard
            title={title ?? __('Media')}
            headerVariant={'light'}
            singleBorder
            // Collapsed, the header is the whole card — drop its divider so it doesn't stack against
            // the card's own bottom border and read as a doubled line.
            hideHeaderDivider={collapsed}
            headerRight={toggle}
          >
            {/* Padding only while open — on a collapsed card it would leave 32px of empty height. */}
            <div className={classnames({ [classes.quoteBody]: !collapsed })}>{body}</div>
          </OutlinedTableCard>
        </div>
      )}
    />
  )
}
